/**
 * Abonnement agrégé pour le récap pack builder (sans formulaire produit).
 * Les fréquences suivent l’index des allocations de la première ligne sélectionnée.
 */
import { formatMoney } from '@theme/utilities';

const EVENT_UPDATE = 'pack-builder-subscription:update';

/** @param {string | undefined} type */
const normalizeAdjustmentType = (type) =>
  String(type ?? 'percentage')
    .toLowerCase()
    .replace(/_/g, '');

/** @param {unknown} raw */
const parseNumeric = (raw) =>
  typeof raw === 'number' ? raw : parseFloat(String(raw ?? '').replace(',', '.')) || 0;

/**
 * Remise % Shopify (centimes) : calculée par unité, discount arrondi vers le bas.
 * @see https://community.shopify.dev/t/inconsistent-cents-rounding-on-discounts-across-different-stores/28655/10
 * @param {number} cents Prix unitaire en centimes
 * @param {number} percentOff
 */
const applyPercentOffCents = (cents, percentOff) => {
  const amount = Math.max(0, Math.round(Number(cents)) || 0);
  const pct = Number(percentOff);
  if (!Number.isFinite(pct) || pct <= 0) return amount;
  if (pct >= 100) return 0;
  const discount = Math.floor((amount * pct) / 100);
  return Math.max(0, amount - discount);
};

/** @param {unknown} alloc */
const allocationPlanName = (alloc) =>
  alloc && typeof alloc === 'object' && 'name' in alloc ? String(/** @type {{ name?: string }} */ (alloc).name ?? '') : '';

function parseJsonScript(el, fallback) {
  try {
    const text = el?.textContent?.trim();
    if (text == null || text === '') return fallback;
    return JSON.parse(text);
  } catch {
    return fallback;
  }
}

class PackBuilderSubscription extends HTMLElement {
  #abortController = null;
  /** @type {Record<string, unknown>} */
  #catalog = {};
  #planIndex = 0;

  connectedCallback() {
    const { signal } = (this.#abortController = new AbortController());
    this.#catalog = this.#readCatalog();
    this.addEventListener('change', this.#onChange, { signal });
    window.addEventListener('pack-builder:selection-changed', this.#onSelectionChanged, { signal });
    this.#sync(true);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  requestFooterUpdate() {
    this.#sync(false);
  }

  buildCartItems(items) {
    if (this.hidden) return items.map((i) => ({ ...i }));

    const mode = this.#getEffectiveMode();
    if (mode !== 'subscription') {
      return items.map((item) => ({ id: item.id, quantity: item.quantity }));
    }

    return items.map((item) => {
      const line = { id: item.id, quantity: item.quantity };
      const entry = this.#catalog[String(item.id)];
      const alloc = this.#allocationForEntry(entry);
      if (alloc?.selling_plan_id) line.selling_plan = alloc.selling_plan_id;
      return line;
    });
  }

  #footerRoot() {
    return this.closest('pack-builder-sticky-footer');
  }

  #readCatalog() {
    return parseJsonScript(this.#footerRoot()?.querySelector('script[data-pack-variant-catalog]'), {});
  }

  /** @returns {{ count: number, label: string }[]} */
  #getPackTierMilestones() {
    const raw = parseJsonScript(this.#footerRoot()?.querySelector('script[data-pack-tier-milestones]'), []);
    if (!Array.isArray(raw)) return [];
    return raw
      .map((m) => ({
        count: typeof m?.count === 'number' ? m.count : 0,
        label: typeof m?.label === 'string' ? m.label : '',
      }))
      .filter((m) => m.count > 0 && m.label.trim() !== '')
      .sort((a, b) => a.count - b.count);
  }

  /** @param {unknown[]} lines */
  #totalQty(lines) {
    return Array.isArray(lines)
      ? lines.reduce((s, line) => s + Math.max(0, parseInt(String(line?.quantity ?? 0), 10) || 0), 0)
      : 0;
  }

  /** Libellé du palier actif (remise déjà débloquée), ou null si aucun palier atteint. */
  #activeTierLabel(milestones, totalQuantity) {
    if (!milestones.length || totalQuantity <= 0) return null;
    const reached = [...milestones]
      .sort((a, b) => b.count - a.count)
      .find((m) => totalQuantity >= m.count);
    if (!reached) return null;
    const s = String(reached.label ?? '')
      .trim()
      .replace(/^-/, '')
      .trim();
    return s || null;
  }

  /**
   * Pourcentage de remise du palier atteint (ex: 15 pour -15%).
   * Retourne 0 si aucun palier n'est atteint.
   */
  #activeTierDiscountPercent(totalQty) {
    const milestones = this.#getPackTierMilestones();
    if (!milestones.length) return 0;
    const reached = [...milestones]
      .sort((a, b) => b.count - a.count)
      .find((m) => totalQty >= m.count);
    if (!reached) return 0;
    const match = String(reached.label ?? '').match(/(\d+(?:\.\d+)?)\s*%/);
    if (!match) return 0;
    const pct = parseFloat(match[1]);
    if (!isFinite(pct) || pct <= 0 || pct >= 100) return 0;
    return pct;
  }

  /**
   * Compare-at au moins égal au prix payé (affichage barré).
   */
  #withCompareFloor(totals) {
    return {
      ...totals,
      compareOneTime: Math.max(totals.compareOneTime, totals.payOneTime),
      compareSub: Math.max(totals.compareSub, totals.paySubscription),
    };
  }

  /** @param {string | null | undefined} tierText */
  #pctDigitsFromTierText(tierText) {
    const m = String(tierText ?? '').trim().match(/(\d+(?:[.,]\d+)?)\s*%?/);
    if (!m) return '';
    const n = parseFloat(m[1].replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? String(Math.round(n)).replace('.', ',') : '';
  }

  #packBuilder() {
    return this.closest('pack-builder');
  }

  /** @returns {{ lines: unknown[] }} */
  #selection() {
    const pb = this.#packBuilder();
    const sel = pb && typeof pb.getSelection === 'function' ? pb.getSelection() : { lines: [] };
    return { lines: Array.isArray(sel.lines) ? sel.lines : [] };
  }

  /** Première ligne du catalogue qui a des selling_plan_allocations. */
  #firstEntryWithAllocations(lines) {
    for (const line of lines) {
      const e = this.#catalog[String(line.variantId)];
      if (e?.selling_plan_allocations?.length) return e;
    }
    return null;
  }

  /** ID du selling plan de référence (fréquence UI). */
  #selectedSellingPlanId() {
    const { lines } = this.#selection();
    const entry = this.#firstEntryWithAllocations(lines);
    const alloc = entry?.selling_plan_allocations?.[this.#planIndex];
    const id = alloc?.selling_plan_id;
    return id == null ? null : id;
  }

  /**
   * Allocation d’abonnement pour une entrée catalogue.
   * - Produit sans selling plans → null (pas d’abo)
   * - Sinon : même selling_plan_id si possible, sinon même index de fréquence, sinon 1er plan
   * @param {Record<string, unknown> | null | undefined} entry
   */
  #allocationForEntry(entry) {
    const allocs = entry?.selling_plan_allocations;
    if (!Array.isArray(allocs) || !allocs.length) return null;

    const selectedPlanId = this.#selectedSellingPlanId();
    if (selectedPlanId != null) {
      const byId = allocs.find((a) => a && Number(a.selling_plan_id) === Number(selectedPlanId));
      if (byId) return byId;
    }

    if (allocs[this.#planIndex]) return allocs[this.#planIndex];
    return allocs[0] || null;
  }

  #currentAllocation() {
    const { lines } = this.#selection();
    const entry = this.#firstEntryWithAllocations(lines);
    return this.#allocationForEntry(entry);
  }

  #onSelectionChanged = (event) => {
    const pb = this.#packBuilder();
    if (!pb || (event.detail?.sectionId && event.detail.sectionId !== pb.dataset.sectionId)) return;
    this.#sync(true);
  };

  #onChange = (event) => {
    if (this.classList.contains('is-empty-selection')) return;

    const target = /** @type {HTMLElement} */ (event.target);
    const fromRadio = target?.matches?.('[data-ref="frequency-input"]') && target instanceof HTMLInputElement && target.checked;
    const fromSelect = target?.matches?.('[data-ref="frequency-select"]') && target instanceof HTMLSelectElement;
    if (fromRadio || fromSelect) {
      this.#planIndex = parseInt(String(fromRadio ? target.value : target.value), 10) || 0;
    }
    this.#sync(false);
  };

  #sync(resetPlanIndex) {
    this.#catalog = this.#readCatalog();
    const pb = this.#packBuilder();
    if (!pb) {
      this.hidden = true;
      this.#setPurchaseOptionsEnabled(false);
      this.#emitUpdate(0, 0, false);
      return;
    }

    const { lines } = this.#selection();
    if (resetPlanIndex) this.#planIndex = 0;

    if (lines.length === 0) {
      this.#resetUiForNoSelection();
      return;
    }

    const totalQty = this.#totalQty(lines);
    const tierPercent = this.#activeTierDiscountPercent(totalQty);
    const totals = this.#computeTotals(lines, tierPercent);

    if (!totals.hasAnyAllocation) {
      this.hidden = true;
      this.#rebuildFrequencyUI([]);
      this.#updatePrices(this.#withCompareFloor(totals));
      this.#updatePromotionalCopy(totals);
      this.#setPurchaseOptionsEnabled(false);
      this.#resetSubscriptionPurchaseOptions({ forceSubscription: false });
      this.#emitUpdate(totals.payOneTime, totals.compareOneTime, false);
      return;
    }

    this.hidden = false;
    this.#resetSubscriptionPurchaseOptions({ forceSubscription: totals.requiresSellingPlan });
    this.#setPurchaseOptionsEnabled(true);
    if (totals.requiresSellingPlan) {
      const sub = this.querySelector('[data-ref="subscription-option"]');
      if (sub instanceof HTMLInputElement) sub.checked = true;
    }

    this.#rebuildFrequencyUI(totals.referenceAllocations);
    const totals2 = this.#computeTotals(lines, tierPercent);
    this.#updatePrices(this.#withCompareFloor(totals2));
    this.#updatePromotionalCopy(totals2);
    this.#updateCardSelection();
    this.#emitEffectiveTotals(totals2);
  }

  #resetUiForNoSelection() {
    // Garde l’UI visible mais désactivée (pas de sélection).
    this.hidden = false;
    this.#planIndex = 0;
    this.#rebuildFrequencyUI([]);
    const empty = { payOneTime: 0, compareOneTime: 0, paySubscription: 0, compareSub: 0 };
    this.#updatePrices(empty);
    this.#updatePromotionalCopy(empty);
    this.#resetSubscriptionPurchaseOptions({ forceSubscription: false });
    this.#applyDefaultRadioSelection();
    this.#setPurchaseOptionsEnabled(false);
    this.#updateCardSelection();
    this.#emitUpdate(0, 0, false);
  }

  /** Active / désactive les options « Achetez une fois » / « Abonnez-vous ». */
  #setPurchaseOptionsEnabled(enabled) {
    this.classList.toggle('is-empty-selection', !enabled);

    const one = this.querySelector('[data-ref="one-time-option"]');
    const sub = this.querySelector('[data-ref="subscription-option"]');
    if (one instanceof HTMLInputElement) {
      // Ne pas réactiver si l’achat unique est forcé masqué (requires selling plan).
      const oneTimeForcedOff = this.querySelector('[data-ref="one-time-card"]')?.classList.contains('hidden');
      one.disabled = !enabled || Boolean(oneTimeForcedOff);
    }
    if (sub instanceof HTMLInputElement) sub.disabled = !enabled;

    this.querySelector('[data-ref="one-time-card"]')?.classList.toggle('is-disabled', !enabled);
    this.querySelector('[data-ref="subscription-card"]')?.classList.toggle('is-disabled', !enabled);
  }

  #resetSubscriptionPurchaseOptions(opts) {
    this.querySelector('[data-ref="one-time-card"]')?.classList.toggle('hidden', opts.forceSubscription);
    const one = this.querySelector('[data-ref="one-time-option"]');
    if (one instanceof HTMLInputElement) one.disabled = opts.forceSubscription;
  }

  #applyDefaultRadioSelection() {
    const subDefault = this.dataset.defaultMode === 'subscription';
    const one = this.querySelector('[data-ref="one-time-option"]');
    const sub = this.querySelector('[data-ref="subscription-option"]');
    if (one instanceof HTMLInputElement && sub instanceof HTMLInputElement) {
      sub.checked = subDefault;
      one.checked = !subDefault;
    }
  }

  /**
   * @param {unknown[]} lines
   * @param {number} [tierPercent] Remise palier : par unité puis × qty (comme Shopify).
   */
  #computeTotals(lines, tierPercent = 0) {
    let payOneTime = 0;
    let compareOneTime = 0;
    let paySubscription = 0;
    let compareSub = 0;
    let hasAnyAllocation = false;
    let requiresSellingPlan = false;
    /** @type {unknown[]} */
    let referenceAllocations = [];

    for (const line of lines) {
      const vid = String(line.variantId);
      const qty = Math.max(1, parseInt(line.quantity, 10) || 1);
      const entry = this.#catalog[vid];
      if (!entry) continue;

      const unit = typeof entry.price === 'number' ? Math.round(entry.price) : 0;
      const cmpRaw = entry.compare_at_price;
      const unitCmp = typeof cmpRaw === 'number' && cmpRaw > unit ? Math.round(cmpRaw) : unit;

      const allocs = Array.isArray(entry.selling_plan_allocations) ? entry.selling_plan_allocations : [];
      if (allocs.length) {
        hasAnyAllocation = true;
        if (!referenceAllocations.length) referenceAllocations = allocs;
      }
      if (entry.requires_selling_plan === true) requiresSellingPlan = true;

      // Remise abo uniquement sur les produits qui ont au moins un selling plan.
      const alloc = this.#allocationForEntry(entry);
      const subUnit = alloc ? this.#subscriptionUnitPriceFromAllocation(unit, alloc) : unit;

      const allocCmp =
        alloc && typeof alloc.compare_at_price === 'number' ? Math.round(alloc.compare_at_price) : unitCmp;

      // Shopify : % par unité (floor), puis × quantité — pas sur le total de ligne.
      payOneTime += applyPercentOffCents(unit, tierPercent) * qty;
      paySubscription += applyPercentOffCents(subUnit, tierPercent) * qty;
      compareOneTime += unitCmp * qty;
      compareSub += Math.max(allocCmp, subUnit) * qty;
    }

    return {
      payOneTime,
      compareOneTime,
      paySubscription,
      compareSub,
      hasAnyAllocation,
      requiresSellingPlan,
      referenceAllocations,
    };
  }

  /**
   * Prix unitaire abonnement en centimes à partir d’une allocation (éligible uniquement).
   * @param {number} unit
   * @param {Record<string, unknown>} alloc
   */
  #subscriptionUnitPriceFromAllocation(unit, alloc) {
    // Prix Shopify déjà calculé pour l’allocation (centimes) — source de vérité.
    if (typeof alloc.price === 'number') return Math.round(alloc.price);

    const t = normalizeAdjustmentType(alloc.adjustment_type);
    const value = parseNumeric(alloc.adjustment_value);

    if (t === 'percentage' || t === 'percent') return applyPercentOffCents(unit, value);
    if (t === 'fixedamount') return Math.max(0, Math.round(unit) - Math.round(value));
    if (t === 'price') return Math.max(0, Math.round(value));
    return unit;
  }

  /** @param {unknown[]} referenceAllocations */
  #rebuildFrequencyUI(referenceAllocations) {
    const inner = this.querySelector('[data-ref="frequency-inner"]');
    const container = this.querySelector('[data-ref="frequency-container"]');
    if (!(inner instanceof HTMLElement) || !(container instanceof HTMLElement)) return;

    inner.innerHTML = '';

    const hide = () => {
      container.classList.add('hidden');
      container.style.display = 'none';
    };

    if (!referenceAllocations?.length || referenceAllocations.length <= 1) {
      hide();
      return;
    }

    if (this.#planIndex >= referenceAllocations.length) this.#planIndex = 0;

    const name = `pack-frequency-${this.dataset.sectionId || 'pack'}`;
    const useButtons = this.dataset.frequencyPickerType === 'buttons';

    if (useButtons) {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'subscription__frequency-buttons';
      referenceAllocations.forEach((alloc, idx) => {
        const inputId = `${name}-${idx}`;
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = name;
        input.value = String(idx);
        input.setAttribute('data-ref', 'frequency-input');
        input.checked = idx === this.#planIndex;
        input.id = inputId;
        const label = document.createElement('label');
        label.setAttribute('for', inputId);
        label.textContent = allocationPlanName(alloc);
        const row = document.createElement('div');
        row.className = 'subscription__frequency-button';
        row.append(input, label);
        fieldset.append(row);
      });
      inner.append(fieldset);
    } else {
      const select = document.createElement('select');
      select.className = 'select select--small';
      select.setAttribute('data-ref', 'frequency-select');
      referenceAllocations.forEach((alloc, idx) => {
        const opt = document.createElement('option');
        opt.value = String(idx);
        opt.textContent = allocationPlanName(alloc);
        opt.selected = idx === this.#planIndex;
        select.append(opt);
      });
      const wrap = document.createElement('div');
      wrap.className = 'select-wrapper select-wrapper--full-width';
      const iconWrap = document.createElement('div');
      iconWrap.className = 'select-wrapper__icon';
      iconWrap.append(this.#matIcon('keyboard_arrow_down'));
      wrap.append(select, iconWrap);
      inner.append(wrap);
    }

    this.#syncFrequencyVisibility();
  }

  #syncFrequencyVisibility() {
    const container = this.querySelector('[data-ref="frequency-container"]');
    const inner = this.querySelector('[data-ref="frequency-inner"]');
    if (!(container instanceof HTMLElement) || !(inner instanceof HTMLElement)) return;

    const hasOptions = inner.children.length > 0;
    const show = this.#getEffectiveMode() === 'subscription' && hasOptions;

    if (show) {
      container.classList.remove('hidden');
      container.style.display = 'block';
      return;
    }

    container.classList.add('hidden');
    container.style.display = 'none';
  }

  /**
   * @param {ReturnType<PackBuilderSubscription['#computeTotals']>} totals
   */
  #updatePrices(totals) {
    this.#updatePriceElement(this.querySelector('[data-ref="one-time-price"]'), totals.payOneTime, totals.compareOneTime, false);
    this.#updatePriceElement(this.querySelector('[data-ref="subscription-price"]'), totals.paySubscription, totals.compareSub, true);
  }

  /**
   * @param {Element | null | undefined} container
   */
  #updatePriceElement(container, price, compareAtPrice, showSubscriptionBadge) {
    if (!container) return;

    const priceEl = container.querySelector('[data-ref="price"]');
    const cmpEl = container.querySelector('[data-ref="compare-at-price"]');
    const badgeEl = container.querySelector('[data-ref="difference-price-value"]');

    if (priceEl) {
      priceEl.textContent = formatMoney(price);
      priceEl.dataset.value = String(price);
    }

    const hasSavings = compareAtPrice > price;
    if (cmpEl) {
      cmpEl.textContent = hasSavings ? formatMoney(compareAtPrice) : '';
      cmpEl.dataset.value = hasSavings ? String(compareAtPrice) : '';
    }
    if (badgeEl) badgeEl.textContent = showSubscriptionBadge ? this.#formatSubscriptionDiscount() : '';
  }

  /**
   * @param {ReturnType<PackBuilderSubscription['#computeTotals']>} totals
   */
  #updatePromotionalCopy(totals) {
    const { lines } = this.#selection();
    const qty = this.#totalQty(lines);
    const milestones = this.#getPackTierMilestones();

    let subPct = this.#subscriptionPctLabel();
    if (!subPct) subPct = this.#pctDigitsFromTierText(this.#activeTierLabel(milestones, qty));

    const showSub = Boolean(subPct);
    const pctEl = this.querySelector('[data-ref="subscription-discount-pct"]');
    const extraEl = this.querySelector('[data-ref="subscription-extra-line"]');
    if (pctEl) pctEl.textContent = showSub ? `${subPct}%` : '';
    extraEl?.classList.toggle('hidden', !showSub);

    let oneTime = this.#activeTierLabel(milestones, qty);
    if (oneTime == null && !milestones.length && totals.compareOneTime > totals.payOneTime && totals.compareOneTime > 0) {
      const p = Math.round(((totals.compareOneTime - totals.payOneTime) / totals.compareOneTime) * 100);
      if (p > 0) oneTime = `${p}%`;
    } else if (oneTime != null && !/%\s*$/.test(oneTime) && /^\d+([.,]\d+)?$/.test(oneTime.replace(/\s/g, ''))) {
      oneTime = `${oneTime.replace(/\s/g, '')}%`;
    }

    const line = this.querySelector('[data-ref="one-time-savings-line"]');
    const strong = this.querySelector('[data-ref="one-time-save-pct"]');
    if (line && strong) {
      if (oneTime) {
        strong.textContent = oneTime;
        line.classList.remove('hidden');
      } else {
        strong.textContent = '';
        line.classList.add('hidden');
      }
    }
  }

  #subscriptionPctValue() {
    const alloc = this.#currentAllocation();
    if (!alloc) return null;
    const t = normalizeAdjustmentType(alloc.adjustment_type);
    const value = parseNumeric(alloc.adjustment_value);
    if ((t !== 'percentage' && t !== 'percent') || value === 0) return null;
    return value;
  }

  #subscriptionPctLabel() {
    const v = this.#subscriptionPctValue();
    return v == null ? '' : String(Math.round(v)).replace('.', ',');
  }

  #formatSubscriptionDiscount() {
    const alloc = this.#currentAllocation();
    if (!alloc) return '';
    const value = parseNumeric(alloc.adjustment_value);
    if (value === 0) return '';

    const badgeType = this.dataset.salesBadge || 'percentage';
    if (badgeType === 'none') return '';
    const suffix = (this.dataset.salesBadgeText || '').trim();

    if (badgeType === 'percentage') {
      return `${value.toFixed(0).replace('.', ',')}%${suffix ? ` ${suffix}` : ''}`;
    }
    return `${formatMoney(value)}${suffix ? ` ${suffix}` : ''}`;
  }

  #updateCardSelection() {
    const mode = this.#getEffectiveMode();
    this.querySelector('[data-ref="one-time-card"]')?.classList.toggle('subscription__option--selected', mode === 'one_time');
    this.querySelector('[data-ref="subscription-card"]')?.classList.toggle('subscription__option--selected', mode === 'subscription');
    this.#syncFrequencyVisibility();
  }

  /** @returns {'one_time' | 'subscription'} */
  #getEffectiveMode() {
    const sub = this.querySelector('[data-ref="subscription-option"]');
    return sub instanceof HTMLInputElement && sub.checked ? 'subscription' : 'one_time';
  }

  /**
   * @param {ReturnType<PackBuilderSubscription['#computeTotals']>} totals
   */
  #emitEffectiveTotals(totals) {
    const mode = this.#getEffectiveMode();
    this.#emitUpdate(
      mode === 'subscription' ? totals.paySubscription : totals.payOneTime,
      mode === 'subscription' ? totals.compareSub : totals.compareOneTime,
      true,
    );
  }

  #emitUpdate(pay, compare, subscriptionUiVisible) {
    const mode = subscriptionUiVisible ? this.#getEffectiveMode() : 'one_time';
    this.dispatchEvent(
      new CustomEvent(EVENT_UPDATE, {
        bubbles: true,
        detail: { pay, compare, mode, subscriptionUiVisible },
      }),
    );
  }

  /** Crée un `<theme-icon>` Material Symbols (mask CSS). */
  #matIcon(name) {
    const el = document.createElement('theme-icon');
    el.className = `icon icon--${name}`;
    el.setAttribute('aria-hidden', 'true');

    const ref = document.querySelector('theme-icon[data-icon-style][data-icon-variant]');
    const iconStyle = ref?.dataset.iconStyle || 'rounded';
    const iconVariant = ref?.dataset.iconVariant || 'default';

    el.dataset.iconStyle = iconStyle;
    el.dataset.iconName = name;
    el.dataset.iconVariant = iconVariant;

    const opticalSize = 24;
    const url = `https://fonts.gstatic.com/s/i/short-term/release/materialsymbols${iconStyle}/${name}/${iconVariant}/${opticalSize}px.svg`;
    el.style.setProperty('--icon-url', `url('${url}')`);

    return el;
  }
}

if (!customElements.get('pack-builder-subscription')) {
  customElements.define('pack-builder-subscription', PackBuilderSubscription);
}
